'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Form } from 'react-bootstrap';
const uuid = require('uuid');
const find = require('lodash/find');
const map = require('lodash/map');

const AdminGlobalUserStore = require('../../stores/AdminGlobalUserStore');
const GlobalUserActions = require('../../actions/GlobalUserActions');
const OIDCStore = require('../../stores/OIDCStore');
const OIDCActions = require('../../actions/OIDCActions');
const ServerRequest = require('../../actions/request');

import LoadingSpinner from '../loading/LoadingSpinner';
import AdminUserLmsDeploymentEditor from './AdminUserLmsDeploymentEditor';
import OpenIdProviderSelector from './OpenIdProviderSelector';

const propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired
};
const defaultProps = {};

class AdminLTISettingsView extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            lmsDeploymentState: AdminGlobalUserStore.getState().lmsDeployments[props.user.id],
            openIdProviders: OIDCStore.getState().openIdProviders,
            showArchived: false,
            justUnarchivedId: null,
        };

        this.onOIDCStoreChange = this.onOIDCStoreChange.bind(this);
        this.setProviderForAddingDeployment = this.setProviderForAddingDeployment.bind(this);
        this.addDeployment = this.addDeployment.bind(this);

        this.unsubscribes = [
            AdminGlobalUserStore.listen(this.onAdminGlobalUserStoreChange),
            OIDCStore.listen(this.onOIDCStoreChange),
        ];
    }

    componentDidMount() {
        this.refreshDeployments();
        OIDCStore.requestDataIfNeeded();
    }

    componentDidUpdate(prevProps) {
        if (this.props.user.id === prevProps.user.id) return;

        this.setState({ showArchived: false }); // eslint-disable-line  @eslint-react/no-set-state-in-component-did-update
        this.refreshDeployments(false);
    }

    refreshDeployments=(showArchived = this.state.showArchived)=>{
        GlobalUserActions.getLmsDeployments(uuid.v4(), this.props.user.id, showArchived);
    }

    // highlight the deployment briefly once it reappears among the active ones
    onUnarchived=(deploymentId)=>{
        clearTimeout(this.highlightTimer);
        this.setState({ justUnarchivedId: deploymentId });
        this.highlightTimer = setTimeout(() => this.setState({ justUnarchivedId: null }), 4000);
    }

    toggleShowArchived=(e)=>{
        const showArchived = e.target.checked;
        this.setState({ showArchived });
        this.refreshDeployments(showArchived);
    }

    componentWillUnmount() {
        clearTimeout(this.highlightTimer);
        this.unsubscribes.forEach(u => u());
    }

    onAdminGlobalUserStoreChange=(state)=>{
        this.setState({ 
            lmsDeploymentState: state.lmsDeployments[this.props.user.id]
        });
    }
    
    onOIDCStoreChange=(state)=>{
        this.setState({ 
            openIdProviders: state.openIdProviders
        });
    }
    
    setProviderForAddingDeployment=(opId)=>{
        this.setState({ selectedOpId: opId });
    }

    addDeployment=(opId)=>{
        opId = this.state.selectedOpId;
        if (!opId) {
            return;
        }

        const op = find(this.state.openIdProviders, { id: String(opId) });
        if (!op) {
            return;
        }

        const deployment_name = op.description || op.iss;
        if (window.confirm("Are you sure you wish to add a new " + deployment_name + " deployment?")) {
            ServerRequest.makeRequest((`/authoring/user/${this.props.user.id}/add/lms_deployment`), 'POST', JSON.stringify({
                    opId: opId
                }))
                .then((res) => {
                    const lmsDeploymentState = this.state.lmsDeploymentState.slice();
                    if (!find(lmsDeploymentState, { id: res.id })) {
                        // put the new record in the list
                        lmsDeploymentState.push(res);
                    }

                    this.setState({ 
                        lmsDeploymentState: lmsDeploymentState, 
                        lmsDeploymentMessageVisible:true, 
                        lmsDeploymentMessageState: 'success' , 
                        lmsDeploymentMessage: 'LMS Deployment Added',
                        selectedOpId: null,
                    });
                })
                .catch((err) => {
                    console.log("error", err)
                    this.setState({
                        lmsDeploymentMessageVisible:true,
                        lmsDeploymentMessageState: 'danger' ,
                        lmsDeploymentMessage: 'There was a problem adding an LMS Deployment',
                        selectedOpId: null,
                    });
                });
        }
    }

    render() {
        if (!this.state.lmsDeploymentState) {
            return (
                <div>
                    <LoadingSpinner />One moment please...
                </div>
            );
        }

        const activeDeployments = this.state.lmsDeploymentState.filter(d => !d.archived);
        // filtering on showArchived here too, in case a response for the other checkbox state arrives late
        const archivedDeployments = this.state.showArchived ? this.state.lmsDeploymentState.filter(d => d.archived) : [];
        const renderEditors = (deployments) => map(deployments, (entry) => (
            <AdminUserLmsDeploymentEditor
                key={entry.id}
                openIdProviders={this.state.openIdProviders}
                deployment={entry}
                user={this.props.user}
                onUpdated={() => this.refreshDeployments()}
                onUnarchived={this.onUnarchived}
                highlight={!entry.archived && String(entry.id) === String(this.state.justUnarchivedId)}
            />
        ));

        return (
            <div>
                <Card body bg="light">
                    <h2 className="section-title">{'LTI 1.3 Deployments'}</h2>
                    <Form.Check
                        type="checkbox"
                        id={`admin-show-archived-deployments-${this.props.user.id}`}
                        label="Show archived deployments"
                        checked={this.state.showArchived}
                        onChange={this.toggleShowArchived}
                        className="margin-bottom-15"
                    />
                    { activeDeployments.length ?
                        renderEditors(activeDeployments)
                        :
                        <div>No active deployments</div>
                    }
                    { this.state.showArchived &&
                        <div className="lms-archived-deployments">
                            <h3 className="lms-archived-heading">Archived</h3>
                            { archivedDeployments.length ?
                                renderEditors(archivedDeployments)
                                :
                                <div>No archived deployments</div>
                            }
                        </div>
                    }
                </Card>
                {(this.state.openIdProviders && this.state.openIdProviders.length ) ? 
                    <div>
                        <OpenIdProviderSelector
                            id="admin-add-deployment-op-selector"
                            openIdProviders={this.state.openIdProviders}
                            labelText="Add New Deployment"
                            dropDown
                            onSelect={(opId) => {this.setProviderForAddingDeployment(opId) }}
                        />
                        <Button onClick={this.addDeployment} disabled={!this.state.selectedOpId}>
                            Add Deployment
                        </Button>

                    </div>
                    : 
                    <div>Add an OpenID provider before adding an LMS deployment</div>
                }
            </div>
        );
    }
}

AdminLTISettingsView.propTypes = propTypes;
AdminLTISettingsView.defaultProps = defaultProps;

export default AdminLTISettingsView;